import { fetchUtils } from 'react-admin';
import { AuthService } from './services/AuthService';
import { TokenManager } from './utils/TokenManager';
import { EnvironmentService } from './services/EnvironmentService';
import { logger } from './utils/logger';
import { CustomDataProvider } from './types/dataProvider';


// HTTP client with automatic token refresh
const httpClient = async (url: string, options: fetchUtils.Options = {}) => {
  const startTime = Date.now();

  let token = TokenManager.getAccessToken();
  // Check if token is available from TokenManager
  
  if (!token) {
    logger.apiWarn('No authentication token available', { url }, 'httpClient');
    const error = new Error('No token');
    (error as any).status = 401;
    throw error;
  }

  // Set authorization header
  if (!options.headers) {
    options.headers = new Headers();
  }
  if (options.headers instanceof Headers) {
    options.headers.set('Authorization', `Bearer ${token}`);
  }
  
  try {
    const response = await fetchUtils.fetchJson(url, options);
    const duration = Date.now() - startTime;
    logger.apiDebug('HTTP request completed', {
      url,
      status: response.status,
      duration: `${duration}ms`,
      method: options.method || 'GET',
      responseData: response.json
    }, 'httpClient');
    return response;
  } catch (error: any) {
    const duration = Date.now() - startTime;
    // Handle 401 errors with retry logic (only if we have refresh capability)
    if (error.status === 401 && !(options as any)._retry && TokenManager.getRefreshToken()) {
      logger.apiInfo('401 detected, attempting token refresh', { url, duration: `${duration}ms` }, 'httpClient');
      
      // Try to refresh token
      const refreshSuccess = await AuthService.refreshAccessToken();
      
      if (refreshSuccess) {
        // Get the new token and retry the request
        const newToken = TokenManager.getAccessToken();
        if (newToken && options.headers instanceof Headers) {
          options.headers.set('Authorization', `Bearer ${newToken}`);
          // Mark as retry to prevent infinite loops
          (options as any)._retry = true;
          return await fetchUtils.fetchJson(url, options);
        }
      }
      
      // Refresh failed or no new token, let React Admin handle logout
      logger.apiError('Token refresh failed, triggering logout', error, 'httpClient');
    } else {
      // Log other errors
      logger.apiError('HTTP request failed', {
        url,
        status: error.status,
        duration: `${duration}ms`,
        method: options.method || 'GET',
        error: error.message
      }, 'httpClient');
    }

    throw error;
  }
};

// Convert React Admin params to Latio API query parameters
export const convertParams = (type: string, params: any, resource: string) => {
  switch (type) {
    case 'GET_LIST':
      const { page, perPage } = params.pagination || { page: 1, perPage: 50 };
      const { field, order } = params.sort || { field: 'created_at', order: 'DESC' };
      const filter = params.filter || {};

      const query = new URLSearchParams({
        page: String(page),
        limit: String(perPage),
        ...(field && { sort_by: field }),
        ...(order && { sort_order: order.toLowerCase() }),
      });

      // Add universal search filter (q parameter)
      if (filter.q) {
        query.set('search', filter.q);
      }

      // Add resource-specific filters
      if (resource === 'users') {
        if (filter.status) query.set('status', filter.status);
        if (filter.is_trainer !== undefined) query.set('is_trainer', filter.is_trainer);
      } else if (resource === 'feedback') {
        if (filter.status) query.set('status', filter.status);
      } else if (resource === 'device-tokens') {
        if (filter.platform) query.set('platform', filter.platform);
        if (filter.active_only !== undefined) query.set('is_active', filter.active_only);
        if (filter.created_after) query.set('created_after', filter.created_after);
        if (filter.last_used_after) query.set('last_used_after', filter.last_used_after);
        if (filter.user_email) query.set('user_email', filter.user_email);
        if (filter.user_name) query.set('user_name', filter.user_name);
      } else if (resource === 'notification-logs') {
        if (filter.notification_type) query.set('notification_type', filter.notification_type);
        if (filter.delivered_only) query.set('delivered_only', filter.delivered_only);
        if (filter.opened_only) query.set('opened_only', filter.opened_only);
        if (filter.clicked_only) query.set('clicked_only', filter.clicked_only);
        if (filter.date_from) query.set('date_from', filter.date_from);
        if (filter.date_to) query.set('date_to', filter.date_to);
        if (filter.user_email) query.set('user_email', filter.user_email);
      } else if (resource === 'trainer-applications') {
        if (filter.status_filter) query.set('status_filter', filter.status_filter);
        if (filter.user_id) query.set('user_id', filter.user_id);
      } else if (resource === 'specialty-certificates') {
        if (filter.status_filter) query.set('status_filter', filter.status_filter);
        if (filter.sport_id) query.set('sport_id', filter.sport_id);
        if (filter.trainer_id) query.set('trainer_id', filter.trainer_id);
      } else if (resource === 'trainers') {
        // Trainers resource filters
        if (filter.status_filter === 'active') {
          query.set('profile_completed', 'true');
        } else if (filter.status_filter === 'incomplete') {
          query.set('profile_completed', 'false');
        } else if (filter.status_filter === 'suspended') {
          query.set('is_suspended', 'true');
        } else if (filter.status_filter === 'banned') {
          query.set('is_banned', 'true');
        }
        if (filter.search) query.set('search', filter.search);
        if (filter.sort_by) query.set('sort_by', filter.sort_by);
      } else if (resource === 'sessions') {
        // Sessions resource filters
        if (filter.session_type) query.set('session_type', filter.session_type);
        if (filter.status) query.set('status', filter.status);
        if (filter.trainer_id) query.set('trainer_id', filter.trainer_id);
        if (filter.sport_id) query.set('sport_id', filter.sport_id);
        if (filter.date_from) query.set('date_from', filter.date_from);
        if (filter.date_to) query.set('date_to', filter.date_to);
        if (filter.search) query.set('search', filter.search);
      } else if (resource === 'session-reviews') {
        // Session reviews filters (backend ignores unknown params → degrades to unfiltered)
        if (filter.review_type) query.set('review_type', filter.review_type);
        if (filter.min_rating) query.set('min_rating', String(filter.min_rating));
        if (filter.max_rating) query.set('max_rating', String(filter.max_rating));
      } else if (resource === 'feedback') {
        // Feedback filters (backend ignores unknown params → degrades to unfiltered)
        if (filter.status) query.set('status', filter.status);
      } else if (resource === 'notifications') {
        // Notification filters (backend ignores unknown params → degrades to unfiltered)
        if (filter.status) query.set('status', filter.status);
        if (filter.notification_type) query.set('notification_type', filter.notification_type);
      } else if (resource === 'bookings') {
        // Bookings resource filters
        if (filter.status) query.set('status', filter.status);
        if (filter.session_id) query.set('session_id', filter.session_id);
        if (filter.user_id) query.set('user_id', filter.user_id);
        if (filter.trainer_id) query.set('trainer_id', filter.trainer_id);
        if (filter.date_from) query.set('date_from', filter.date_from);
        if (filter.date_to) query.set('date_to', filter.date_to);
      } else if (resource === 'direct-chats') {
        if (filter.user_id) query.set('user_id', String(filter.user_id));
        if (filter.has_messages !== undefined) query.set('has_messages', String(filter.has_messages));
        if (filter.min_messages) query.set('min_messages', String(filter.min_messages));
      } else if (resource === 'payments') {
        if (filter.status) query.set('status', filter.status);
      } else if (resource === 'refunds') {
        if (filter.payment_id) query.set('payment_id', String(filter.payment_id));
      } else if (resource === 'payment-webhooks') {
        if (filter.processed !== undefined) query.set('processed', String(filter.processed));
        if (filter.payment_id) query.set('payment_id', String(filter.payment_id));
      }

      return `?${query.toString()}`;

    default:
      return '';
  }
};

// Custom data provider for Latio backend
export const dataProvider: CustomDataProvider = {
  getList: async (resource, params) => {
    // Map React Admin resource names to Latio admin endpoints
    const resourceMap: { [key: string]: string } = {
      'users': 'admin/users',
      'feedback': 'admin/feedback',
      'locations': 'admin/locations', // If available
      'session-reviews': 'admin/session-reviews',
      'device-tokens': 'admin/device-tokens',
      'notifications': 'admin/notifications',
      'notification-logs': 'admin/notification-logs',
      'session-chats': 'admin/session-chat',
      'trainer-applications': 'trainer/admin/applications',
      'trainers': 'trainer/admin/trainers',
      'sessions': 'admin/sessions',
      'bookings': 'admin/bookings',
      'sports': 'admin/sports',
      'direct-chats': 'admin/direct-chats',
      'specialty-certificates': 'admin/specialty-certificates',
    };

    const endpoint = resourceMap[resource] || `admin/${resource}`;
    const url = `${EnvironmentService.getApiUrl()}/${endpoint}${convertParams('GET_LIST', params, resource)}`;


    try {
      const { json } = await httpClient(url);

      // Enhanced logging for sessions debugging
      if (resource === 'sessions') {
        logger.apiDebug('Sessions API response', {
          response: json,
          url,
          dataType: typeof json.data,
          isArray: Array.isArray(json.data),
          dataKeys: Object.keys(json.data || {}),
          data: json.data,
          firstSession: json.data?.sessions?.[0],
        }, 'DataProvider');
      }

      // Handle notification-logs direct format: { items: [...], total: N }
      if (resource === 'notification-logs' && json.items && typeof json.total === 'number') {
        return {
          data: json.items.map((item: any) => ({ ...item, id: item.id })),
          total: json.total,
        };
      }
      
      // Handle Latio backend response format: { status: "success", data: { users: [...], total: N } }
      if (json.status === 'success') {
        // Handle resource key mapping for response data
        let resourceKey: string | null = resource;
        if (resource === 'feedback') {
          resourceKey = 'feedback';
        } else if (resource === 'session-reviews') {
          resourceKey = 'session_reviews';
        } else if (resource === 'device-tokens') {
          resourceKey = 'device_tokens'; // Backend might use snake_case
        } else if (resource === 'session-chats') {
          resourceKey = 'session_chats'; // Backend returns session_chats key
        } else if (resource === 'trainer-applications') {
          // Backend returns data directly in array format
          resourceKey = null; // Handle as direct array
        } else if (resource === 'trainers') {
          // Backend returns data directly in array format
          resourceKey = null; // Handle as direct array
        } else if (resource === 'sports') {
          // Backend returns data directly in array format
          resourceKey = null; // Handle as direct array
        } else if (resource === 'specialty-certificates') {
          // Backend returns data directly in array format
          resourceKey = null; // Handle as direct array
        } else if (resource === 'sessions') {
          resourceKey = 'sessions'; // Backend returns sessions key
        } else if (resource === 'bookings') {
          resourceKey = 'bookings'; // Backend returns bookings key
        } else if (resource === 'direct-chats') {
          resourceKey = 'chats';
        } else if (resource === 'payment-webhooks') {
          resourceKey = 'webhooks';
        }

        const data = resourceKey === null ? json.data : (json.data[resourceKey] || json.data[resource] || json.data.items || []);
        const total = json.data.pagination?.total || json.data.total || data.length;

        const mappedData = data.map((item: any) => {
          // For session-chats, ensure we have an id field
          if (resource === 'session-chats') {
            logger.apiDebug('Session-chat item from backend', { item }, 'DataProvider');
            // Backend might not include 'id', so we need to create one from available data
            return {
              ...item,
              id: item.id || item.session_id || item.chat_id
            };
          }

          // For sessions, map backend field names to frontend field names
          if (resource === 'sessions') {
            return {
              ...item,
              id: item.id,
              scheduled_at: item.date_time, // Map date_time to scheduled_at
              trainer: item.trainer_id ? {
                id: item.trainer_id,
                name: item.trainer_name,
                email: item.trainer_email,
                profile_picture_url: item.trainer_profile_picture,
              } : undefined,
              sport: item.sport_id ? {
                id: item.sport_id,
                name: item.sport_name,
              } : undefined,
              location: item.location_name ? {
                id: item.location_id,
                name: item.location_name,
                address: item.location_address,
              } : undefined,
            };
          }

          // For bookings, map backend field names to frontend field names
          if (resource === 'bookings') {
            return {
              ...item,
              id: item.id,
              created_at: item.joined_at, // Map joined_at to created_at
              user: item.user_id ? {
                id: item.user_id,
                name: item.user_name,
                email: item.user_email,
                username: item.user_username,
                phone: item.user_phone,
              } : undefined,
              session: item.session_id ? {
                id: item.session_id,
                session_type: item.session_type,
                title: item.session_title,
                scheduled_at: item.session_date_time,
                status: item.session_status,
              } : undefined,
              trainer: item.trainer_id ? {
                id: item.trainer_id,
                name: item.trainer_name,
                email: item.trainer_email,
              } : undefined,
              sport: item.sport_name ? {
                name: item.sport_name,
              } : undefined,
              price_paid: item.session_price ? parseFloat(item.session_price) : undefined,
            };
          }

          if (resource === 'direct-chats') {
            return { ...item, id: item.chat_id };
          }

          return { ...item, id: item.id };
        });

        return {
          data: mappedData,
          total,
        };
      } else {
        throw new Error(json.message || 'Failed to fetch data');
      }
    } catch (error) {
      logger.apiError(`Failed to fetch ${resource}`, { error, url }, 'DataProvider');
      // Re-throw 401 errors to trigger automatic logout
      if (error instanceof Error && error.message.includes('401')) {
        throw error;
      }
      // For other HTTP errors, check if it's from fetchJson
      if (typeof error === 'object' && error !== null && 'status' in error && error.status === 401) {
        throw error;
      }
      // Return empty data for other errors to prevent dashboard crashes
      return { data: [], total: 0 };
    }
  },

  getOne: async (resource, params) => {
    try {
      const resourceMap: { [key: string]: string } = {
        'users': 'admin/users',
        'feedback': 'admin/feedback',
        'session-reviews': 'admin/session-reviews',
        'notifications': 'admin/notifications',
        'notification-logs': 'admin/notification-logs',
        'session-chats': 'admin/session-chat',
        'trainer-applications': 'trainer/admin/applications',
        'trainers': 'trainer/admin/trainers',
        'sessions': 'admin/sessions',
        'bookings': 'admin/bookings',
        'sports': 'admin/sports',
        'direct-chats': 'admin/direct-chats',
        'specialty-certificates': 'admin/specialty-certificates',
      };

      const endpoint = resourceMap[resource] || `admin/${resource}`;
      const url = `${EnvironmentService.getApiUrl()}/${endpoint}/${params.id}`;
      const { json } = await httpClient(url);
      
      // Handle notification-logs direct format
      if (resource === 'notification-logs' && json.id) {
        return { data: json };
      }
      
      if (json.status === 'success') {
        let data = json.data;

        if (resource === 'direct-chats') {
          data = { ...data, id: data.chat_id };
        }

        // For session-chats, force the id to match the requested id (session_id from URL)
        if (resource === 'session-chats') {
          logger.apiDebug('Session-chat getOne data from backend', { data }, 'DataProvider');
          // Force the id to be the session_id that was requested, not the chat record id
          data = { ...data, id: parseInt(params.id as string), session_id: data.session_id || parseInt(params.id as string) };
        }

        // For sessions, map backend field names to frontend field names
        if (resource === 'sessions') {
          data = {
            ...data,
            scheduled_at: data.date_time, // Map date_time to scheduled_at
            trainer: data.trainer_id ? {
              id: data.trainer_id,
              name: data.trainer_name,
              email: data.trainer_email,
              profile_picture_url: data.trainer_profile_picture,
            } : undefined,
            sport: data.sport_id ? {
              id: data.sport_id,
              name: data.sport_name,
            } : undefined,
            location: data.location_name ? {
              id: data.location_id,
              name: data.location_name,
              address: data.location_address,
            } : undefined,
            requester: data.requested_by_user_id ? {
              id: data.requested_by_user_id,
              name: data.requested_by_name,
              email: data.requested_by_email,
              phone: data.requested_by_phone,
            } : undefined,
            // The backend sends flat counts; the detail page's Bookings card
            // reads booking_stats, which nothing produced, so it never rendered.
            booking_stats: {
              total_bookings: data.total_bookings ?? 0,
              active_bookings: data.confirmed_bookings ?? 0,
              cancelled_bookings: data.cancelled_bookings ?? 0,
              removed_bookings: data.removed_bookings ?? 0,
            },
          };
        }

        // For bookings, map backend field names to frontend field names
        if (resource === 'bookings') {
          data = {
            ...data,
            created_at: data.joined_at, // Map joined_at to created_at
            user: data.user_id ? {
              id: data.user_id,
              name: data.user_name,
              email: data.user_email,
              username: data.user_username,
              phone: data.user_phone,
            } : undefined,
            session: data.session_id ? {
              id: data.session_id,
              session_type: data.session_type,
              title: data.session_title,
              scheduled_at: data.session_date_time,
              status: data.session_status,
            } : undefined,
            trainer: data.trainer_id ? {
              id: data.trainer_id,
              name: data.trainer_name,
              email: data.trainer_email,
            } : undefined,
            sport: data.sport_name ? {
              name: data.sport_name,
            } : undefined,
            price_paid: data.session_price ? parseFloat(data.session_price) : undefined,
          };
        }

        return { data };
      } else {
        throw new Error(json.message || 'Failed to fetch record');
      }
    } catch (error) {
      logger.apiError(`Failed to fetch ${resource} ${params.id}`, { error }, 'DataProvider');
      // Always throw errors for getOne to maintain proper error handling
      throw error;
    }
  },

  getMany: async (resource, params) => {
    // Not commonly used in Latio backend, but implement basic version
    try {
      const requests = params.ids.map((id: any) => 
        dataProvider.getOne(resource, { id })
      );
      const responses = await Promise.all(requests);
      return { data: responses.map(response => response.data) };
    } catch (error) {
      // Re-throw 401 errors to trigger automatic logout
      if (typeof error === 'object' && error !== null && 'status' in error && error.status === 401) {
        throw error;
      }
      return { data: [] };
    }
  },

  getManyReference: async (resource, params) => {
    // For related records - use same as getList with additional filter
    const newParams = {
      ...params,
      filter: { ...params.filter, [params.target]: params.id }
    };
    return dataProvider.getList(resource, newParams);
  },

  create: async (resource, params) => {
    try {
      let url: string;
      let body: any;

      // Handle specific create endpoints for Latio backend
      if (resource === 'sports') {
        url = `${EnvironmentService.getApiUrl()}/admin/sports`;
        body = params.data;
      } else if (resource === 'notifications') {
        url = `${EnvironmentService.getApiUrl()}/admin/notifications`;
        body = {
          title: params.data.title,
          message: params.data.message,
          notification_type: params.data.notification_type,
          target_criteria: {
            segments: Array.isArray(params.data['target_criteria.segments']) 
              ? params.data['target_criteria.segments'] 
              : [params.data['target_criteria.segments'] || 'all']
          },
          ...(params.data.scheduled_for && { scheduled_for: params.data.scheduled_for })
        };
      } else {
        throw new Error(`Create operation not implemented for ${resource}`);
      }

      const { json } = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      // Handle notifications direct response format
      if (resource === 'notifications' && json.id) {
        return { data: json };
      }

      if (json.status === 'success') {
        return { data: json.data };
      } else {
        throw new Error(json.message || 'Create failed');
      }
    } catch (error) {
      logger.apiError(`Failed to create ${resource}`, { error }, 'DataProvider');
      // Always throw errors for create operations to maintain proper error handling
      throw error;
    }
  },

  update: async (resource, params) => {
    try {
      let url: string;
      let body: any;

      // Handle specific update endpoints for Latio backend
      if (resource === 'sports') {
        url = `${EnvironmentService.getApiUrl()}/admin/sports/${params.id}`;
        body = params.data;
      } else if (resource === 'users') {
        url = `${EnvironmentService.getApiUrl()}/admin/users/${params.id}/status`;
        body = {
          is_active: params.data.is_active,
          is_admin: params.data.is_admin,
        };
      } else if (resource === 'feedback') {
        url = `${EnvironmentService.getApiUrl()}/admin/feedback/${params.id}/status`;
        body = { status: params.data.status };
      } else if (resource === 'trainer-applications') {
        url = `${EnvironmentService.getApiUrl()}/trainer/admin/applications/${params.id}/review`;
        body = {
          approved: params.data.approved,
          ...(params.data.rejection_reason && { rejection_reason: params.data.rejection_reason })
        };
      } else if (resource === 'specialty-certificates') {
        url = `${EnvironmentService.getApiUrl()}/admin/specialty-certificates/${params.id}/review`;
        body = {
          approved: params.data.approved,
          ...(params.data.rejection_reason && { rejection_reason: params.data.rejection_reason })
        };
      } else if (resource === 'sessions') {
        url = `${EnvironmentService.getApiUrl()}/admin/sessions/${params.id}/cancel`;
        body = {
          reason: params.data.cancellation_reason,
          notify_participants: params.data.notify_participants !== false
        };
      } else if (resource === 'bookings') {
        url = `${EnvironmentService.getApiUrl()}/admin/bookings/${params.id}/status`;
        body = {
          status: params.data.status,
          admin_notes: params.data.admin_notes
        };
      } else {
        throw new Error(`Update operation not implemented for ${resource}`);
      }

      const method = ['trainer-applications', 'specialty-certificates'].includes(resource) ? 'POST' : 'PUT';

      const { json } = await httpClient(url, {
        method: method,
        body: JSON.stringify(body),
      });

      if (json.status === 'success') {
        // Return the updated record with all previous data plus new data
        return { data: { ...params.previousData, ...params.data, id: params.id } as any };
      } else {
        throw new Error(json.message || 'Update failed');
      }
    } catch (error) {
      logger.apiError(`Failed to update ${resource}`, { error }, 'DataProvider');
      // Always throw errors for update operations to maintain proper error handling
      throw error;
    }
  },

  updateMany: async (resource, params) => {
    const requests = params.ids.map((id: any) =>
      dataProvider.update(resource, { id, data: params.data, previousData: {} })
    );
    const responses = await Promise.all(requests);
    return { data: responses.map(response => response.data.id) };
  },

  delete: async (resource, params) => {
    try {
      if (resource === 'users') {
        const url = `${EnvironmentService.getApiUrl()}/admin/users/${params.id}`;
        const { json } = await httpClient(url, { method: 'DELETE' });

        if (json.status === 'success') {
          return { data: params.previousData as any };
        } else {
          throw new Error(json.message || 'Delete failed');
        }
      } else if (resource === 'sports') {
        const url = `${EnvironmentService.getApiUrl()}/admin/sports/${params.id}`;
        const { json } = await httpClient(url, { method: 'DELETE' });

        if (json.status === 'success') {
          return { data: params.previousData as any };
        } else {
          throw new Error(json.message || 'Delete failed');
        }
      } else if (resource === 'bookings') {
        const url = `${EnvironmentService.getApiUrl()}/admin/bookings/${params.id}`;
        const { json } = await httpClient(url, { method: 'DELETE' });

        if (json.status === 'success') {
          return { data: params.previousData as any };
        } else {
          throw new Error(json.message || 'Delete failed');
        }
      } else if (resource === 'session-reviews') {
        const url = `${EnvironmentService.getApiUrl()}/admin/session-reviews/${params.id}`;
        const { json } = await httpClient(url, { method: 'DELETE' });

        if (json.status === 'success') {
          return { data: params.previousData as any };
        } else {
          throw new Error(json.message || 'Delete failed');
        }
      } else if (resource === 'device-tokens') {
        const url = `${EnvironmentService.getApiUrl()}/admin/device-tokens/${params.id}`;
        const { json } = await httpClient(url, { method: 'DELETE' });

        if (json.status === 'success') {
          return { data: params.previousData as any };
        } else {
          throw new Error(json.message || 'Delete failed');
        }
      } else if (resource === 'locations') {
        const url = `${EnvironmentService.getApiUrl()}/admin/locations/${params.id}`;
        const { json } = await httpClient(url, { method: 'DELETE' });

        if (json.status === 'success') {
          return { data: params.previousData as any };
        } else {
          throw new Error(json.message || 'Delete failed');
        }
      } else {
        throw new Error(`Delete operation not implemented for ${resource}`);
      }
    } catch (error) {
      logger.apiError(`Failed to delete ${resource}`, { error }, 'DataProvider');
      // Always throw errors for delete operations to maintain proper error handling
      throw error;
    }
  },

  deleteMany: async (resource, params) => {
    const requests = params.ids.map((id: any) =>
      dataProvider.delete(resource, { id, previousData: { id } })
    );
    await Promise.all(requests);
    return { data: params.ids };
  },

  // ── Dashboard ──────────────────────────────────────────────────────

  getDashboardStats: async () => {
    const url = `${EnvironmentService.getApiUrl()}/admin/dashboard/stats`;
    const { json } = await httpClient(url);
    if (json.status === 'success') {
      return { data: json.data };
    }
    throw new Error(json.message || 'Failed to fetch dashboard stats');
  },

  getPlatformAnalytics: async (params) => {
    const days = params.days ?? 30;
    const url = `${EnvironmentService.getApiUrl()}/admin/analytics/platform?days=${days}`;
    const { json } = await httpClient(url);
    if (json.status === 'success') {
      return { data: json.data };
    }
    throw new Error(json.message || 'Failed to fetch platform analytics');
  },

  getRecentSessions: async (params) => {
    const limit = params.limit ?? 10;
    const url = `${EnvironmentService.getApiUrl()}/admin/sessions?page=1&limit=${limit}&sort_by=created_at&sort_order=desc`;
    const { json } = await httpClient(url);
    const sessions = Array.isArray(json) ? json : (json.items || json.data?.sessions || []);
    return { data: sessions };
  },

  // ── Chat Monitoring ────────────────────────────────────────────────
  // BE only exposes /admin/session-chat/* — legacy /admin/chat/* routes do not exist.

  getChatStats: async () => {
    const url = `${EnvironmentService.getApiUrl()}/admin/session-chat/stats`;
    const { json } = await httpClient(url);
    if (json.status === 'success') {
      return { data: json.data };
    }
    throw new Error(json.message || 'Failed to fetch chat stats');
  },

  getChatMonitoring: async () => {
    const url = `${EnvironmentService.getApiUrl()}/admin/session-chat/monitoring`;
    const { json } = await httpClient(url);
    if (json.status === 'success') {
      return { data: json.data };
    }
    throw new Error(json.message || 'Failed to fetch chat monitoring data');
  },

  // ── Direct Chat Admin ─────────────────────────────────────────────

  getDirectChatStats: async () => {
    const url = `${EnvironmentService.getApiUrl()}/admin/direct-chats/stats`;
    const { json } = await httpClient(url);
    if (json.status === 'success') {
      return { data: json.data };
    }
    throw new Error(json.message || 'Failed to fetch direct chat stats');
  },

  getDirectChatMessages: async (params) => {
    const { chatId, page = 1, limit = 50, includeDeleted = false } = params;
    const url = `${EnvironmentService.getApiUrl()}/admin/direct-chats/${chatId}/messages?page=${page}&limit=${limit}&include_deleted=${includeDeleted}`;
    const { json } = await httpClient(url);
    if (json.status === 'success') {
      return { data: json.data };
    }
    throw new Error(json.message || 'Failed to fetch direct chat messages');
  },

  softDeleteDirectChatMessage: async (params) => {
    const { chatId, messageId, reason } = params;
    const url = `${EnvironmentService.getApiUrl()}/admin/direct-chats/${chatId}/messages/${messageId}`;
    const { json } = await httpClient(url, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    });
    if (json.status === 'success') {
      return { data: json.data };
    }
    throw new Error(json.message || 'Failed to delete message');
  },

  // ── User Actions ──────────────────────────────────────────────────

  suspendUser: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/trainer/admin/users/${params.id}/suspend`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({ reason: params.reason, notes: params.notes }),
    });
    return { data: json.data || json };
  },

  unsuspendUser: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/trainer/admin/users/${params.id}/unsuspend`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({ reason: 'Unsuspended by admin', notes: 'User account suspension lifted' }),
    });
    return { data: json.data || json };
  },

  banUser: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/trainer/admin/users/${params.id}/ban`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({ reason: params.reason, notes: params.notes }),
    });
    return { data: json.data || json };
  },

  // ── Trainer Application Actions ───────────────────────────────────

  markApplicationUnderReview: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/trainer/admin/applications/${params.id}/mark-under-review`;
    const { json } = await httpClient(url, { method: 'POST' });
    return { data: json.data || json };
  },

  requestApplicationResubmission: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/trainer/admin/applications/${params.id}/request-resubmission`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({ reason: params.reason, notes: params.notes }),
    });
    return { data: json.data || json };
  },

  suspendApplication: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/trainer/admin/applications/${params.id}/suspend`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({ reason: params.reason, notes: params.notes }),
    });
    return { data: json.data || json };
  },

  revokeApplication: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/trainer/admin/applications/${params.id}/revoke`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({ reason: params.reason, notes: params.notes }),
    });
    return { data: json.data || json };
  },

  banApplication: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/trainer/admin/applications/${params.id}/ban`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({ reason: params.reason, notes: params.notes }),
    });
    return { data: json.data || json };
  },

  reinstateApplication: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/trainer/admin/applications/${params.id}/reinstate`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({ reason: 'Reinstated by admin', notes: 'Trainer privileges restored' }),
    });
    return { data: json.data || json };
  },

  getApplicationHistory: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/trainer/admin/applications/${params.id}/history`;
    const { json } = await httpClient(url);
    return { data: json.data || [] };
  },

  // ── Session Actions ───────────────────────────────────────────────

  cancelSession: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/admin/sessions/${params.id}/cancel`;
    const { json } = await httpClient(url, {
      method: 'PUT',
      body: JSON.stringify({
        reason: params.reason,
        notify_participants: params.notify_participants ?? true,
      }),
    });
    return { data: json.data || json };
  },

  reactivateSessionHold: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/admin/sessions/${params.id}/reactivate-hold`;
    try {
      const { json } = await httpClient(url, { method: 'POST' });
      if (json.status === 'success') return { data: json.data };
      throw new Error(json.message || 'Reactivation failed');
    } catch (e: any) {
      // Refused (wrong status, wrong session type) comes back as a 400 whose
      // reason is in FastAPI's `detail` - same unwrap as releasePayment below,
      // otherwise the admin sees a bare "Bad Request".
      const detail = e?.body?.detail;
      if (detail) {
        throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      }
      throw e;
    }
  },

  getSessionChatMessages: async (params) => {
    const page = params.page ?? 1;
    const limit = params.limit ?? 1000;
    const includeDeleted = params.includeDeleted ?? false;
    const url = `${EnvironmentService.getApiUrl()}/admin/session-chat/sessions/${params.sessionId}/messages?page=${page}&limit=${limit}&sort_by=created_at&sort_order=asc&include_deleted=${includeDeleted}`;
    const { json } = await httpClient(url);
    if (json.status === 'success') {
      return { data: json.data };
    }
    throw new Error(json.message || 'Failed to fetch session chat messages');
  },

  actionSessionChatMessage: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/admin/session-chat/messages/${params.messageId}/action`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({ action: params.action, reason: params.reason }),
    });
    return { data: json.data || json };
  },

  // ── Booking Actions ───────────────────────────────────────────────

  updateBookingStatus: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/admin/bookings/${params.id}/status`;
    const { json } = await httpClient(url, {
      method: 'PUT',
      body: JSON.stringify({ status: params.status, admin_notes: params.admin_notes }),
    });
    return { data: json.data || json };
  },

  deleteBooking: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/admin/bookings/${params.id}`;
    const { json } = await httpClient(url, { method: 'DELETE' });
    return { data: json.data || json };
  },

  // FEAT-030: take a player off a group class. Audited server-side; the
  // player can't rejoin. 409 when the booking has an approved/in-flight
  // payment (refund it first). The reason is admin-only.
  removeBooking: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/admin/bookings/${params.id}/remove`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({ reason: params.reason ?? null }),
    });
    return { data: json.data || json };
  },

  // ── App Version Management ────────────────────────────────────────

  getAppVersion: async (params) => {
    // Public endpoint, but admin is always authenticated here so we reuse httpClient
    const url = `${EnvironmentService.getApiUrl()}/app/version-check/?platform=${params.platform}`;
    const { json } = await httpClient(url);
    if (json.status === 'success') {
      return { data: json.data };
    }
    // Public endpoints may return the payload directly
    if (json && (json.min_version || json.latest_version || json.store_url)) {
      return { data: json };
    }
    throw new Error(json.message || 'Failed to fetch app version');
  },

  updateAppVersion: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/admin/app/version/${params.platform}`;
    const { json } = await httpClient(url, {
      method: 'PUT',
      body: JSON.stringify(params.data),
    });
    if (json.status === 'success') {
      return { data: json.data };
    }
    throw new Error(json.message || 'Failed to update app version');
  },

  // ── Sessions Stats ────────────────────────────────────────────────

  getSessionsStatsOverview: async () => {
    const url = `${EnvironmentService.getApiUrl()}/admin/sessions/stats/overview`;
    const { json } = await httpClient(url);
    if (json.status === 'success') {
      return { data: json.data };
    }
    throw new Error(json.message || 'Failed to fetch sessions stats');
  },

  getTrainerSessions: async (params) => {
    const page = params.page ?? 1;
    const limit = params.limit ?? 25;
    const query = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (params.status) query.set('status', params.status);
    const url = `${EnvironmentService.getApiUrl()}/admin/trainers/${params.id}/sessions?${query.toString()}`;
    const { json } = await httpClient(url);
    if (json.status === 'success') {
      const data = json.data?.sessions || json.data?.items || (Array.isArray(json.data) ? json.data : []);
      const total = json.data?.pagination?.total ?? json.data?.total ?? data.length;
      return { data, total };
    }
    throw new Error(json.message || 'Failed to fetch trainer sessions');
  },

  // ── Notification Analytics ────────────────────────────────────────

  getNotificationAnalytics: async (params) => {
    const days = params.days ?? 30;
    const url = `${EnvironmentService.getApiUrl()}/admin/notifications/analytics?days=${days}`;
    const { json } = await httpClient(url);
    if (json.status === 'success') {
      return { data: json.data };
    }
    throw new Error(json.message || 'Failed to fetch notification analytics');
  },

  getNotificationTypes: async () => {
    const url = `${EnvironmentService.getApiUrl()}/notifications/types`;
    const { json } = await httpClient(url);
    if (json.status === 'success') {
      return { data: json.data.types };
    }
    throw new Error(json.message || 'Failed to fetch notification types');
  },

  // ── Sports Stats ──────────────────────────────────────────────────

  getSportsStatsOverview: async () => {
    const url = `${EnvironmentService.getApiUrl()}/admin/sports/stats/overview`;
    const { json } = await httpClient(url);
    if (json.status === 'success') {
      const data = json.data?.sports || (Array.isArray(json.data) ? json.data : []);
      return { data };
    }
    throw new Error(json.message || 'Failed to fetch sports stats');
  },

  // ── User Activity ─────────────────────────────────────────────────

  getUserActivity: async (params) => {
    const days = params.days ?? 30;
    const url = `${EnvironmentService.getApiUrl()}/admin/users/${params.id}/activity?days=${days}`;
    const { json } = await httpClient(url);
    if (json.status === 'success') {
      return { data: json.data };
    }
    throw new Error(json.message || 'Failed to fetch user activity');
  },

  // ── System Health ─────────────────────────────────────────────────

  getSystemHealth: async () => {
    const url = `${EnvironmentService.getApiUrl()}/admin/system/health`;
    const { json } = await httpClient(url);
    if (json.status === 'success') {
      return { data: json.data };
    }
    throw new Error(json.message || 'Failed to fetch system health');
  },

  // ── Payment Actions ───────────────────────────────────────────────

  refundPayment: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/admin/payments/${params.id}/refund`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({ amount: params.amount ?? null, reason: params.reason }),
    });
    if (json.status === 'success') return { data: json.data };
    throw new Error(json.message || 'Refund failed');
  },

  settlePayout: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/admin/payouts/${params.trainerId}/settle`;
    const { json } = await httpClient(url, {
      method: 'POST',
      body: JSON.stringify({
        transfer_reference: params.transfer_reference,
        admin_notes: params.admin_notes,
      }),
    });
    if (json.status === 'success') return { data: json.data };
    throw new Error(json.message || 'Settle failed');
  },

  reprocessWebhook: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/admin/payment-webhooks/${params.id}/reprocess`;
    const { json } = await httpClient(url, { method: 'POST' });
    if (json.status === 'success') return { data: json.data };
    throw new Error(json.message || 'Reprocess failed');
  },

  getFeatureFlags: async () => {
    const url = `${EnvironmentService.getApiUrl()}/admin/feature-flags`;
    const { json } = await httpClient(url);
    if (json.status === 'success') return { data: json.data };
    throw new Error(json.message || 'Failed to load feature flags');
  },

  updateFeatureFlag: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/admin/feature-flags/${params.key}`;
    const { json } = await httpClient(url, {
      method: 'PATCH',
      body: JSON.stringify({ enabled: params.enabled }),
    });
    if (json.status === 'success') return { data: json.data };
    throw new Error(json.message || 'Failed to update feature flag');
  },

  releasePayment: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/admin/payments/${params.id}/release`;
    try {
      const { json } = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify({
          force: params.force ?? false,
          admin_notes: params.admin_notes,
        }),
      });
      if (json.status === 'success') return { data: json.data };
      throw new Error(json.message || 'Release failed');
    } catch (e: any) {
      // The endpoint refuses any release it cannot prove is safe — "payment is
      // approved, the money was taken", "Wompi refused to void" — with a 400
      // whose reason is in FastAPI's `detail`. react-admin's fetchJson builds
      // its HttpError message from `json.message` only, so without this unwrap
      // the admin sees a bare "Bad Request" in place of the one sentence that
      // tells them not to force it. Same handling as sendTestEmail below.
      const detail = e?.body?.detail;
      if (detail) {
        throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
      }
      throw e;
    }
  },

  sendTestEmail: async (params) => {
    const url = `${EnvironmentService.getApiUrl()}/admin/system/email-test?recipient=${encodeURIComponent(params.recipient)}`;
    try {
      const { json } = await httpClient(url, { method: 'POST' });
      if (json.status === 'success') {
        return { data: json.data };
      }
      throw new Error(json.message || 'Failed to send test email');
    } catch (e: any) {
      // FastAPI returns 502 on SMTP failure with { detail: { error, recipient } }
      const detail = e?.body?.detail;
      if (detail) {
        const msg =
          typeof detail === 'string'
            ? detail
            : detail.error || JSON.stringify(detail);
        throw new Error(msg);
      }
      throw e;
    }
  },
};

export default dataProvider;